// The demo (`?demo`): the real Store and sync on a server in memory (src/data/memoryStore.ts), seeded with the demo
// account, and a sign-in that signs in at once. What the Playwright tests use. Options in the address: `free` (no
// Pro), `signedOut` (the sign-in screen first), `syncBehind=09:17` (the cloud in the list's bar, docs/64: not synced
// since 09:17 today; with `syncOffline` offline; with `syncRecovers` Try Again brings it in step), `empty` (an account
// with nothing in it), `newer` (marked by a newer app: the update note).

import { splitEmoji } from "../../core/areaIcons";
import { addDays, at, currentZone, daysBetween, startOfDay, today as todayIn, weekStart } from "../../core/days";
import { dayTodo, isFlexible, isScheduled, refreshHabitDays } from "../../core/habits";
import { makeHabit, type Habit, type Todo } from "../../core/records";
import type { Day } from "../../core/types";
import { codecs, encodeFavorites, type CollectionName, type Fields } from "../../core/records";
import { memoryStore } from "../../data/memoryStore";
import type { SyncStatus } from "../../core/syncStatus";
import { currentLanguage } from "../../i18n";
import type { AuthActions, User } from "../model";
import { StoreModel } from "../storeModel";
import { memoryMail } from "../../data/mail";
import { memorySharing } from "../../data/memorySharing";
import { DEMO_UID, sampleAccount } from "./sample";

export interface DemoOptions {
  signedIn?: boolean;
  pro?: boolean;
  empty?: boolean;
  /** The account marked by a newer app (feature level 4, beyond habits): the update note shows. */
  newer?: boolean;
  /** One area without an icon, as an older version leaves it, on an account whose icons were given (docs/61). */
  noIcon?: boolean;
  /** Habits with a history, as on the boards (docs/63; the kit's `-habitsDemo`). */
  habits?: boolean;
  /** With the habits: Practise Spanish 29 days in a row, open today, so its tick reaches the 30-day milestone. */
  milestone?: boolean;
  /**
   * With the habits (boards 9–12, 2026-10-31): Sport 4 times a week, done on this week's Monday, Tuesday and Thursday
   * and in each of the five weeks before, so a tick on a later day closes the week (6 weeks in a row); Swim 3 times a
   * week, nothing this week yet (a tick says "1 of 3"); Yoga 10 times a month (the ring in place of dots).
   */
  weekDone?: boolean;
  /** The cloud in the list's bar (docs/64; the kit's `-syncBehind 09:17`): "09:17", today. */
  syncBehind?: string;
  syncOffline?: boolean;
  syncRecovers?: boolean;
}

export function demoOptionsFrom(search: string): DemoOptions {
  const params = new URLSearchParams(search);
  const options: DemoOptions = { signedIn: !params.has("signedOut"), pro: !params.has("free"), empty: params.has("empty"), newer: params.has("newer"), noIcon: params.has("noIcon"), habits: params.has("habits"), milestone: params.has("milestone"), weekDone: params.has("weekDone") };
  const behind = params.get("syncBehind");
  if (behind) Object.assign(options, { syncBehind: behind, syncOffline: params.has("syncOffline"), syncRecovers: params.has("syncRecovers") });
  return options;
}

const UID = DEMO_UID;

/** The demo account as the server stores it: one document per record, under `users/DEMO`. */
export function demoDocuments(options: DemoOptions = {}): Record<string, Fields> {
  const documents: Record<string, Fields> = {};
  const user: Fields = { datesConverted: true };
  if (options.pro !== false) user.premium = { active: true };
  if (options.newer) user.suggestedFeatureLevel = 4;
  if (!options.empty) {
    const zone = currentZone();
    const account = sampleAccount(todayIn(zone), zone);
    const put = <Name extends CollectionName>(collection: Name, records: readonly { id: string }[]) => {
      const codec = codecs[collection] as { encode(record: unknown): Fields };
      for (const record of records) documents[`users/${UID}/${collection}/${record.id}`] = codec.encode(record);
    };
    put("todos", account.todos);
    put("projects", account.projects);
    put("areas", options.noIcon ? account.areas.map((area) => (area.order === 3 ? { ...area, title: splitEmoji(area.title).name } : area)) : account.areas);
    if (options.noIcon) user.areaIconsAddedAt = new Date("2026-10-20T08:00:00Z");
    put("sections", account.sections);
    put("birthdays", account.birthdays);
    put("templates", account.templates);
    if (options.habits) {
      const { habits, todos } = demoHabits(todayIn(zone), zone, account.areas.find((area) => area.title.includes("Health"))?.id,
        account.areas.find((area) => area.title.includes("Home"))?.id, options.milestone, options.weekDone);
      put("habits", habits);
      put("todos", todos);
    }
    for (const share of account.shares) {
      documents[`shares/${share.id}`] = {
        kind: share.kind, recordID: share.recordID, title: share.title, ownerUID: share.ownerUID,
        memberUIDs: Object.keys(share.members), members: share.members,
      };
      for (const project of share.projects) documents[`shares/${share.id}/projects/${project.id}`] = codecs.projects.encode(project);
      for (const todo of share.todos) documents[`shares/${share.id}/todos/${todo.id}`] = codecs.todos.encode(todo);
      if (share.placement && share.members[UID]) documents[`users/${UID}/placements/${share.id}`] = { ...share.placement };
    }
    for (const invitation of account.invitations) {
      const { id, ...fields } = invitation;
      documents[`invitations/${id}`] = { ...fields, toUID: UID };
    }
    Object.assign(user, encodeFavorites(account.favorites));
  } else {
    Object.assign(user, encodeFavorites([]));
  }
  documents[`users/${UID}`] = user;
  return documents;
}

/**
 * The habits of the boards (the kit's `Store.addHabitsDemo`): Stretch every day (Thursday's miss kept, done today),
 * Read 3 times a week (2 of 3), Run on Tue, Thu and Sat, Practise Spanish (missed yesterday).
 */
export function demoHabits(today: Day, zone: string, health: string | undefined, personal: string | undefined, milestone = false, weekDone = false): { habits: Habit[]; todos: Todo[] } {
  const habits: Habit[] = [];
  const todos: Todo[] = [];
  const now = new Date();
  const make = (number: number, title: string, area: string | undefined, schedule: Habit["schedule"], days: number, done: (back: number, day: Day) => boolean) => {
    const start = addDays(today, -days);
    // Ids that differ in their first bytes: a day's to-do mixes the day into the last ones (habitTodoID).
    const habit = makeHabit({ id: `7${number}A1B2C3-0000-4000-8000-000000000700`, title, schedule, startsOn: start, order: number, createdAt: startOfDay(start, zone) });
    if (area) habit.areaID = area;
    for (let back = 1; back <= days; back++) {
      const day = addDays(today, -back);
      if (!done(back, day)) continue;
      if (!isFlexible(schedule) && !isScheduled(schedule, day, start)) continue;
      todos.push(dayTodo(habit, day, zone, at(day, 19, 0, zone)));
    }
    habits.push(habit);
    return habit;
  };
  const stretch = make(1, "Stretch for 10 minutes", health, { kind: "everyNDays", interval: 1 }, 40, (back) => (back <= 12 && back !== 2) || back > 14);
  const monday = weekStart(today, 2);
  make(2, "Read 20 pages", personal, { kind: "timesPerWeek", count: 3 }, 42, (_, day) => (day >= monday ? daysBetween(day, today) <= 2 : [0, 2, 4].includes(daysBetween(weekStart(day, 2), day))));
  make(3, "Run 5 km", health, { kind: "weekdays", weekdays: [3, 5, 7] }, 30, () => true);
  if (milestone) make(4, "Practise Spanish", personal, { kind: "everyNDays", interval: 1 }, 29, () => true);
  else make(4, "Practise Spanish", personal, { kind: "everyNDays", interval: 1 }, 20, (back) => back > 1);
  if (weekDone) {
    // Begun on a Monday five weeks ago, so every week is whole.
    const weeks = daysBetween(addDays(monday, -35), today);
    const weekdayOf = (day: Day) => daysBetween(weekStart(day, 2), day);
    make(5, "Sport", health, { kind: "timesPerWeek", count: 4 }, weeks, (_, day) => (day >= monday ? [0, 1, 3].includes(weekdayOf(day)) : weekdayOf(day) < 4));
    make(6, "Swim", health, { kind: "timesPerWeek", count: 3 }, weeks, (_, day) => day < monday && [0, 2, 4].includes(weekdayOf(day)));
    make(7, "Yoga", personal, { kind: "timesPerMonth", count: 10 }, weeks, (back) => back <= 9 && back !== 5);
  }
  const refreshed = refreshHabitDays(habits, todos, today, zone);
  for (const todo of refreshed.saved) todos.push(todo.habitID === stretch.id ? { ...todo, completedAt: now } : todo);
  return { habits, todos };
}

/** `syncBehind=09:17`: the cloud as if the browser had been out of reach since 09:17 today. */
function simulatedSync(options: DemoOptions): { status: SyncStatus; recovers: boolean } | undefined {
  const [hour, minute] = (options.syncBehind ?? "").split(":").map(Number);
  if (hour === undefined || minute === undefined || Number.isNaN(hour) || Number.isNaN(minute)) return undefined;
  const zone = currentZone();
  const since = at(todayIn(zone), hour, minute, zone).getTime();
  return { status: { kind: options.syncOffline ? "offline" : "notSynced", since }, recovers: options.syncRecovers === true };
}

export function demoModel(options: DemoOptions = {}): StoreModel {
  const { store, remote } = memoryStore({ uid: UID, documents: demoDocuments(options), language: currentLanguage(), looseProjectsTitle: () => "Projects" });
  void store.start();
  const user = (provider: User["provider"], email = "demo@todolix.app"): User => ({ uid: UID, name: "Bastian", email, provider });
  let model: StoreModel;
  const auth: AuthActions = {
    signInWithApple: async () => model.setUser(user("apple")),
    signInWithGoogle: async () => model.setUser(user("google")),
    signInWithEmail: async (email) => model.setUser(user("password", email)),
    createAccount: async (email) => model.setUser(user("password", email)),
    resetPassword: async () => {},
    signOut: async () => model.setUser(null),
    deleteAccount: async () => {
      await model.deleteAllData();
      model.setUser(null);
    },
  };
  model = new StoreModel({
    store, auth, user: options.signedIn === false ? null : user("apple"), mail: memoryMail(options.pro === false ? [] : [
      { local: "inbox-k7m2q9xd", kind: "inbox", label: "Inbox", active: true },
      { local: "website-relaunch-4fq8k2", kind: "project", targetID: "00000000-0000-4000-8000-000000000010", label: "Website relaunch", active: true },
    ]),
    sharing: memorySharing(remote, UID, { name: "Bastian", email: "demo@todolix.app" }),
    ...(simulatedSync(options) ? { simulatedSync: simulatedSync(options)! } : {}),
  });
  return model;
}
